import { createComponent } from '@/helpers/alpine';
import { debounce } from 'lodash-es';
import prestashop from 'prestashop';
import wretch from 'wretch';
import QueryStringAddon from 'wretch/addons/queryString';
import AbortAddon from 'wretch/addons/abort';
import { TEntries, THTMLElementEvent } from '~types/common';
import { EL, Helpers } from '@/helpers';
import { AplineDispatchedEvent } from 'alpinejs';
import FormDataAddon from 'wretch/addons/formData';
import { IGenericObject, TUpdateCartResponse } from '~types/common';

type TElasticSearchOpts = {
    active?: boolean;
};

type TQueryResult = {
    products: TProductHitRaw[];
    pagination: {
        total_items: number;
    };
    related_categories: TSuggestion[];
};

type TSuggestion = {
    label: string;
    value: string;
};

type TImage = {
    position: number;
    legend: string;
    image_url: string;
};

type TProductHitRaw = {
    name: string;
    reference: string;
    url: string;
    qty_combination: number;
    regular_price_amount: number;
    price_amount: number;
    regular_price: string;
    price: string;
    has_discount: boolean;
    discount_percentage: string;
    discount_amount_to_display: string;
    discount_type: string;
    image_url: string;
    image_legend: string;
    images: TImage[];
    add_to_cart_url: string;
};

type TProductHit = {
    name: string;
    reference: string;
    url: string;
    qty_combination: number;
    price: string;
    regular_price: string;
    price_amount: number;
    regular_price_amount: number;
    has_discount: boolean;
    discount_percentage: string;
    discount_amount_to_display: string;
    discount_type: string;
    image_url: string;
    image_legend: string;
    images: TImage[];
    add_to_cart_url: string;
};

function displayPrice(price: number, taxLabel: boolean = true) {
    const tax = prestashop.configuration.display_prices_tax_incl ? 'TTC' : 'HT';
    return [
        Intl.NumberFormat(prestashop.language.locale, {
            style: 'currency',
            currency: prestashop.currency.iso_code,
        }).format(price),
        taxLabel ? tax : null,
    ]
        .filter(Boolean)
        .join(' ');
}

const hitTransformer = ({
    name,
    reference,
    url,
    price_amount,
    regular_price_amount,
    has_discount,
    qty_combination,
    discount_percentage,
    discount_amount_to_display,
    discount_type,
    image_url,
    image_legend,
    images,
    add_to_cart_url,
}: TProductHitRaw): TProductHit => ({
    name,
    reference,
    url,
    qty_combination,
    price: displayPrice(price_amount),
    regular_price: displayPrice(regular_price_amount, false),
    price_amount,
    regular_price_amount,
    has_discount,
    discount_percentage,
    discount_amount_to_display,
    discount_type,
    image_url,
    image_legend,
    images,
    add_to_cart_url,
});

const Search = createComponent((opts: TElasticSearchOpts) => ({
    active: opts.active ?? true,
    controller: null as AbortController | null,
    state: false,
    query: '',
    hits: [] as TProductHit[],
    total: 0,
    suggestions: [] as TSuggestion[],
    isScrolled: false,

    get isOpen() {
        return this.state;
    },

    get isClosed() {
        return !this.state;
    },

    reset() {
        this.controller?.abort();
        this.hits = [];
        this.suggestions = [];
        this.total = 0;
    },

    open() {
        this.toggle(true);
        this.isScrolled = window.scrollY > 0;
        document.body.classList.add('overflow-hidden');
    },

    close() {
        this.toggle(false);
        this.reset();
        this.query = '';
        document.body.classList.remove('overflow-hidden');
        this.$dispatch('cart-close-modal');
    },

    toggle(force?: boolean | undefined) {
        this.state = force !== undefined ? force : !this.state;

        this.$nextTick(() =>
            this.state ? this.$refs.input.focus() : this.$refs.input.blur(),
        );
    },

    handleKeyboardEvent(event: KeyboardEvent) {
        if (event.key == 'Escape') {
            this.close();
        }
    },

    parseSuggestions(datas: TSuggestion[], query: string) {
        datas.forEach((element) => {
            element.value = element.label;
            element.label = this.boldQuery(query, element.label);
        });
        return datas;
    },

    boldQuery(query: string, label: string) {
        query = query.toLowerCase();
        const lowerLabel = label.toLowerCase();
        const index = lowerLabel.indexOf(query);
        if (index === 0) {
            return (
                '<b>' +
                label.slice(0, query.length) +
                '</b>' +
                label.slice(query.length)
            );
        } else if (index > 0) {
            return (
                label.slice(0, index) +
                '<b>' +
                label.slice(index, index + query.length) +
                '</b>' +
                label.slice(index + query.length)
            );
        }
        return label;
    },

    async onSearch() {
        if (this.query.length == 0 || this.query == '') {
            this.reset();
            return;
        }

        const [controller, request] = wretch(prestashop.urls.pages.search, {
            cache: 'no-cache',
        })
            .addon(QueryStringAddon)
            .addon(AbortAddon())
            .options({})
            .query({
                s: this.query,
                ajax: 1,
            })
            .get()
            .controller();

        this.controller = controller;
        const results = await request.json<TQueryResult>();

        this.suggestions = this.parseSuggestions(
            results.related_categories,
            this.query,
        );
        this.hits = results.products.map<TProductHit>(hitTransformer);
        this.total = results.pagination.total_items;
    },

    async addToCart(url: string, action: string, body: IGenericObject = {}) {
        const data = await wretch(url)
            .addon(FormDataAddon)
            .addon(AbortAddon())
            .headers({
                Accept: 'application/json',
            })
            .formData({
                ajax: '1',
                action: 'update',
                ...body,
            })
            .post()
            .json<TUpdateCartResponse>();

        prestashop.emit('updateCart', {
            reason: {
                idProduct: data.id_product,
                idProductAttribute: data.id_product_attribute,
                idCustomization: data.id_customization,
                linkAction: action,
                cart: data.cart,
            },
            resp: data,
        });
    },

    init() {
        window.addEventListener('keyup', this.handleKeyboardEvent.bind(this));

        if (this.active) {
            this.$watch('query', debounce(this.onSearch.bind(this), 150));
            this.onSearch();
        }
    },
}));

type TUpdatedProductListKeys =
    | 'rendered_facets'
    | 'rendered_lateral_facets'
    | 'rendered_products_top'
    | 'rendered_products'
    | 'rendered_products_bottom'
    | 'rendered_products_header';

type TUpdatedProductListData = {
    [key in TUpdatedProductListKeys]: string;
};

const FACETS_REPLACE_MAP: { [key in TUpdatedProductListKeys]: string } = {
    rendered_products_header: '#js-product-list-header',
    rendered_facets: '#js-facets',
    rendered_lateral_facets: '#js-lateral-facets',
    rendered_products_top: '#js-product-list-top',
    rendered_products: '#js-product-list',
    rendered_products_bottom: '#js-product-list-bottom',
};

const Facets = createComponent(() => ({
    isFacetsOpen: false,
    openGroups: [] as string[],
    sortedby: '' as string,
    groups: [] as string[],

    facetsOpen() {
        this.isFacetsOpen = true;
    },

    facetsClose() {
        this.isFacetsOpen = false;
        this.openGroups = [];

        this.$dispatch('facets-close');
    },

    groupOpen(group: string) {
        if (this.isGroupOpen(group)) {
            return;
        }

        this.openGroups.push(group);
        this.$dispatch(`facets-group-open-${group}`);
    },

    groupsOpenAll() {
        this.groups.forEach((group) => {
            if (this.isGroupOpen(group)) {
                return;
            }

            this.openGroups.push(group);
            this.$dispatch(`facets-group-open-${group}`);
        });
    },

    groupClose(group: string) {
        this.openGroups.slice(this.openGroups.indexOf(group), 1);
    },

    isGroupOpen(group: string) {
        return this.openGroups.includes(group);
    },

    onInput(event: THTMLElementEvent<HTMLInputElement, InputEvent>) {
        let facetUrl = event.target.value;
        const isHex =
            /^[0-9a-f]+$/i.test(facetUrl) && facetUrl.length % 2 === 0;

        if (isHex) facetUrl = Helpers.String.hex2bin(facetUrl);

        prestashop.emit('updateFacets', facetUrl);
    },

    updateFacets(url: string) {
        prestashop.emit('updateFacets', url);
    },

    onUpdateProductListDOM(data: TUpdatedProductListData) {
        (<TEntries<typeof FACETS_REPLACE_MAP>>(
            Object.entries(FACETS_REPLACE_MAP)
        )).forEach(([key, selector]) => {
            EL.replaceHTML(selector, data[key]);
        });
    },

    init() {
        window.addEventListener('facets-update', (event) => {
            const facetUrl = Helpers.String.hex2bin(
                (<AplineDispatchedEvent>event).detail as string,
            );

            prestashop.emit('updateFacets', facetUrl);

            window.scrollTo({
                top: this.$el.offsetTop - 16,
                behavior: 'smooth',
            });
        });

        window.addEventListener('popstate', () => {
            prestashop.emit('updateFacets', window.location.href);
        });

        prestashop.on('updateProductList', (data: TUpdatedProductListData) => {
            this.onUpdateProductListDOM(data);
        });
    },
}));

const Elastic = {
    Facets,
    Search,
};

export { Elastic };
